import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { ResponsiveImage, IconZoomIn, IconZoomOut } from '../../../components';
import { propTypes } from '../../../util/types';

import css from './ZoomableImage.module.css';

const ZOOM_LEVELS = [1, 1.5, 2, 3];
const MIN_ZOOM = 0;
const MAX_ZOOM = ZOOM_LEVELS.length - 1;

/**
 * ZoomableImage component with zoom and pan controls
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className - Custom class that extends the default class
 * @param {string?} props.rootClassName - Custom class that overrides the default class
 * @param {propTypes.image} props.image - The image object from SDK
 * @param {Array<string>} props.imageVariants - The image variant names to use
 * @param {string} props.alt - Alt text for the image
 * @param {Function?} props.onZoomChange - Callback when zoom level changes
 * @returns {JSX.Element} zoomable image component
 */
const ZoomableImage = props => {
  const { className, rootClassName, image, imageVariants, alt, onZoomChange } = props;

  const [zoomIndex, setZoomIndex] = useState(MIN_ZOOM); // Current zoom level index
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 }); // Pan offset
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const currentZoom = ZOOM_LEVELS[zoomIndex];
  const isZoomedIn = currentZoom > 1;

  // Reset pan position when zoom changes
  useEffect(() => {
    if (currentZoom === 1) {
      setPanPosition({ x: 0, y: 0 });
    }
  }, [currentZoom]);

  // Reset zoom and pan when image changes
  useEffect(() => {
    setZoomIndex(MIN_ZOOM);
    setPanPosition({ x: 0, y: 0 });
  }, [image?.id]);

  // Notify parent of zoom changes
  useEffect(() => {
    if (onZoomChange) {
      onZoomChange(currentZoom);
    }
  }, [currentZoom, onZoomChange]);

  const handleZoomIn = () => {
    if (zoomIndex < MAX_ZOOM) {
      setZoomIndex(zoomIndex + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomIndex > MIN_ZOOM) {
      setZoomIndex(zoomIndex - 1);
    }
  };

  // Mouse drag handlers for panning (used for desktop)
  const handleMouseDown = e => {
    if (!isZoomedIn) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - panPosition.x,
      y: e.clientY - panPosition.y,
    });
  };

  const handleMouseMove = e => {
    if (!isDragging || !isZoomedIn) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // Calculate boundaries to prevent panning too far
    if (containerRef.current && imageRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const imageRect = imageRef.current.getBoundingClientRect();

      const maxX = (imageRect.width - containerRect.width) / 2;
      const maxY = (imageRect.height - containerRect.height) / 2;

      setPanPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers with proper passive: false handling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = e => {
      if (!isZoomedIn) return;
      if (e.touches.length > 1) {
        e.preventDefault(); // Prevent pinch zoom
        return;
      }
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - panPosition.x,
        y: touch.clientY - panPosition.y,
      });
    };

    const handleTouchMove = e => {
      if (!isDragging || !isZoomedIn) return;
      if (e.touches.length > 1) return;

      e.preventDefault(); // Prevent scrolling while panning

      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;

      // Calculate boundaries to prevent panning too far
      if (containerRef.current && imageRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const imageRect = imageRef.current.getBoundingClientRect();

        const maxX = (imageRect.width - containerRect.width) / 2;
        const maxY = (imageRect.height - containerRect.height) / 2;

        setPanPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    // Add touch listeners with passive: false to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isZoomedIn, isDragging, dragStart, panPosition]);

  const classes = classNames(rootClassName || css.root, className);
  const imageContainerClasses = classNames(css.imageContainer, {
    [css.dragging]: isDragging,
    [css.zoomed]: isZoomedIn,
  });

  const imageStyle = {
    transform: `scale(${currentZoom}) translate(${panPosition.x / currentZoom}px, ${
      panPosition.y / currentZoom
    }px)`,
    cursor: isZoomedIn ? (isDragging ? 'grabbing' : 'grab') : 'default',
  };

  return (
    <div className={classes}>
      {/* Zoom Controls */}
      <div className={css.zoomControls}>
        <button
          className={css.zoomButton}
          onClick={handleZoomIn}
          disabled={zoomIndex >= MAX_ZOOM}
          aria-label="Zoom in"
          type="button"
        >
          <IconZoomIn className={css.zoomIcon} />
        </button>
        <button
          className={css.zoomButton}
          onClick={handleZoomOut}
          disabled={zoomIndex <= MIN_ZOOM}
          aria-label="Zoom out"
          type="button"
        >
          <IconZoomOut className={css.zoomIcon} />
        </button>
      </div>

      {/* Image Container with Pan Support */}
      <div
        ref={containerRef}
        className={imageContainerClasses}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div ref={imageRef} style={imageStyle} className={css.imageWrapper}>
          <ResponsiveImage
            rootClassName={css.image}
            image={image}
            alt={alt}
            variants={imageVariants}
            sizes="100vw"
          />
        </div>
      </div>
    </div>
  );
};

export default ZoomableImage;
